/**
 * <AddToHostClientsModal> - Iter 9.40 Pass I
 *
 * Shown only when visitor is a host AND their primary_category matches the
 * card's category. Two paths:
 *   - Search existing client on their dashboard → merge prefs
 *   - Create new client pre-filled from the visible card
 *
 * Writes to POST /api/preference-share/:cardId/add-to-host-clients
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Search, Plus, Loader2, UserPlus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddToHostClientsModal({ open, onOpenChange, cardId, prefill, onAdded }) {
  const [tab, setTab] = useState("search");
  const [q, setQ] = useState("");
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [serviceName, setServiceName] = useState("");
  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

  useEffect(() => {
    if (!open) return;
    if (prefill) {
      setEmail(prefill.email || "");
      setPhone(prefill.phone || "");
      setServiceName(prefill.service_name || "");
    }
  }, [open, prefill]);

  useEffect(() => {
    if (!open || tab !== "search") return;
    setLoading(true);
    fetch(`${API}/me/host-clients-for-link${q ? `?q=${encodeURIComponent(q)}` : ""}`, { headers: auth() })
      .then((r) => r.ok ? r.json() : { clients: [] })
      .then((d) => { setClients(d.clients || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [open, q, tab]);

  const submit = async (body) => {
    const res = await fetch(`${API}/preference-share/${cardId}/add-to-host-clients`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...auth() },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const d = await res.json();
      toast.success(d.action === "created" ? "Client added to your dashboard" : "Merged into existing client");
      onAdded?.(d);
      onOpenChange?.(false);
    } else {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-to-host-clients-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-4 h-4 text-purple-500" /> Add this client to my dashboard
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b mb-3">
          <button onClick={() => setTab("search")} className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === "search" ? "border-purple-500 text-purple-700" : "border-transparent text-gray-500"}`} data-testid="athc-tab-search">Search existing</button>
          <button onClick={() => setTab("create")} className={`px-3 py-1.5 text-xs font-medium border-b-2 ${tab === "create" ? "border-purple-500 text-purple-700" : "border-transparent text-gray-500"}`} data-testid="athc-tab-create">Create new</button>
        </div>

        {tab === "search" && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name..." className="pl-9" data-testid="athc-search-input" />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-1">
              {loading ? (
                <div className="py-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline text-gray-400" /></div>
              ) : clients.length > 0 ? (
                clients.map((c) => (
                  <button
                    key={c.rsvp_id}
                    onClick={() => submit({ action: "existing", target_rsvp_id: c.rsvp_id })}
                    className="w-full text-left p-2 rounded-lg border border-gray-200 hover:border-purple-400 hover:bg-purple-50"
                    data-testid={`athc-client-${c.rsvp_id}`}
                  >
                    <p className="text-sm font-medium">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.email || ""} · {c.service_name || "-"}</p>
                  </button>
                ))
              ) : (
                <p className="py-6 text-center text-xs text-gray-400">No clients found.</p>
              )}
            </div>
          </div>
        )}

        {tab === "create" && (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Client name (from card)</Label>
              <Input value={prefill?.name || ""} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="client@example.com" data-testid="athc-create-email" />
            </div>
            <div>
              <Label className="text-xs">Phone (optional)</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1…" data-testid="athc-create-phone" />
            </div>
            <div>
              <Label className="text-xs">Service</Label>
              <Input value={serviceName} onChange={(e) => setServiceName(e.target.value)} placeholder="e.g. Bridal Glam" data-testid="athc-create-service" />
            </div>
            <Button
              className="w-full"
              onClick={() => submit({ action: "create", email, phone, service_name: serviceName })}
              data-testid="athc-create-submit"
            >
              <Plus className="w-4 h-4 mr-1" /> Add to my clients
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
